/**
 * Pure helpers for the Staff screen (prototype spec §9.13): a member's state,
 * the roster's filters, and what a change of roles does to their permissions.
 */
import type {
  Permission,
  PermissionInfo,
  PermissionOverride,
  Role,
  StaffMember,
} from '@contract';

/** How a role reads on screen. */
export const ROLE_LABELS: Record<Role, string> = {
  support: 'Support',
  operations: 'Operations',
  finance: 'Finance',
  compliance: 'Compliance',
  admin: 'Admin',
  auditor: 'Auditor',
  wall_display: 'Wall display',
};

/** A line under a role that needs one when it is offered. */
export const ROLE_HINTS: Partial<Record<Role, string>> = {
  wall_display:
    'Can only show the SOC and Contact centre walls. No customer data.',
};

/**
 * A staff member who can only show the walls. They go straight to a wall, see
 * no navigation, and the browser never signs them out for being idle.
 */
export function isWallOnly(
  staff: Pick<StaffMember, 'is_super_admin' | 'permissions'>,
): boolean {
  return (
    !staff.is_super_admin &&
    staff.permissions.length > 0 &&
    staff.permissions.every((permission) => permission === 'wall.view')
  );
}

export const STAFF_STATES = [
  'active',
  'not_signed_in',
  'invite_pending',
  'disabled',
] as const;

export type StaffState = (typeof STAFF_STATES)[number];

export const STAFF_STATE_LABEL: Record<StaffState, string> = {
  active: 'Active',
  not_signed_in: 'Not signed in yet',
  invite_pending: 'Invite pending',
  disabled: 'Disabled',
};

export function staffState(
  member: Pick<StaffMember, 'disabled_at' | 'has_login' | 'last_sign_in_at'>,
): StaffState {
  if (member.disabled_at) return 'disabled';
  if (!member.has_login) return 'invite_pending';
  return member.last_sign_in_at ? 'active' : 'not_signed_in';
}

export interface StaffFilters {
  role?: Role;
  status?: StaffState;
  q?: string;
}

/** The roster narrowed by role, state and a name or email search. */
export function filterStaff(
  rows: readonly StaffMember[],
  filters: StaffFilters,
): StaffMember[] {
  const q = filters.q?.trim().toLowerCase();
  return rows.filter((member) => {
    if (filters.role && !member.roles.includes(filters.role)) return false;
    if (filters.status && staffState(member) !== filters.status) return false;
    if (q) {
      const hay = `${member.display_name} ${member.email}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

/**
 * The permissions held with these roles and overrides: the union of the roles,
 * then a grant adds and a revoke removes (what the server does).
 */
export function effectivePermissions(
  roles: readonly Role[],
  rolePermissions: Readonly<Record<string, readonly Permission[]>>,
  overrides: readonly PermissionOverride[],
): Permission[] {
  const held = new Set<Permission>();
  for (const role of roles)
    for (const permission of rolePermissions[role] ?? []) held.add(permission);
  for (const override of overrides) {
    if (override.effect === 'grant') held.add(override.permission);
    else held.delete(override.permission);
  }
  return [...held];
}

export interface PermissionDiff {
  added: Permission[];
  removed: Permission[];
}

export function diffPermissions(
  before: readonly Permission[],
  after: readonly Permission[],
): PermissionDiff {
  const had = new Set(before);
  const has = new Set(after);
  return {
    added: after.filter((permission) => !had.has(permission)),
    removed: before.filter((permission) => !has.has(permission)),
  };
}

function permissions(count: number): string {
  return `${count} ${count === 1 ? 'permission' : 'permissions'}`;
}

/** "Adds 4 permissions, removes 2", "Removes 2 permissions", or "No change to permissions". */
export function diffSentence(diff: PermissionDiff): string {
  const adds = diff.added.length;
  const removes = diff.removed.length;
  if (adds > 0 && removes > 0)
    return `Adds ${permissions(adds)}, removes ${removes}`;
  if (adds > 0) return `Adds ${permissions(adds)}`;
  if (removes > 0) return `Removes ${permissions(removes)}`;
  return 'No change to permissions';
}

/** True when two role lists hold the same roles. */
export function sameRoles(a: readonly Role[], b: readonly Role[]): boolean {
  return a.length === b.length && a.every((role) => b.includes(role));
}

export interface AreaGroup {
  area: string;
  permissions: PermissionInfo[];
}

/** The held permissions grouped by the area the catalogue files them under. */
export function groupByArea(
  held: readonly Permission[],
  catalogue: readonly PermissionInfo[],
): AreaGroup[] {
  const heldSet = new Set(held);
  const groups = new Map<string, PermissionInfo[]>();
  for (const info of catalogue) {
    if (!heldSet.has(info.key)) continue;
    groups.set(info.area, [...(groups.get(info.area) ?? []), info]);
  }
  return [...groups].map(([area, list]) => ({ area, permissions: list }));
}
